<script setup lang="ts">
import type { GroupCandidateExpanded } from '../types'
import { INVESTMENT_TYPE_LABELS } from '../types'

const TYPE_LABELS = INVESTMENT_TYPE_LABELS

defineProps<{
  candidate: GroupCandidateExpanded
  interactive?: boolean
}>()

function getInitials(name: string): string {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getGroupSubtitle(candidate: GroupCandidateExpanded): string {
  const parts: string[] = []
  if (candidate?.investment_types_interested?.length) {
    const label = TYPE_LABELS[candidate.investment_types_interested[0]] || candidate.investment_types_interested[0]
    if (label) parts.push(label)
  }
  if (candidate?.investment_stage) parts.push(candidate.investment_stage)
  return parts.join(' · ')
}

function getLowestMin(candidate: GroupCandidateExpanded): number | null {
  const mins = [candidate.fund_investment_min, candidate.spv_investment_min, candidate.deal_investment_min].filter(
    (v) => v != null && v > 0,
  )
  return mins.length ? Math.min(...mins) : null
}

function formatCompactMoney(amount: number): string {
  if (amount >= 1_000_000) return `$${amount / 1_000_000}M`
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`
  return `$${amount}`
}

function getMinInvestmentLabel(candidate: GroupCandidateExpanded): string {
  const range = candidate.fund_investment_range || candidate.spv_investment_range || candidate.deal_investment_range
  if (range) {
    const lower = range.split(' - ')[0]
    return `Min: ${lower}`
  }
  const min = getLowestMin(candidate)
  if (min) return `Min: ${formatCompactMoney(min)}`
  return ''
}

function getAccreditationLabel(level: string): string {
  const labels: Record<string, string> = {
    accredited: 'Accredited',
    qualified_purchaser: 'Qualified Purchaser',
    non_accredited: 'Non-Accredited',
  }
  return labels[level] || ''
}

function getIndustriesLabel(industries: string[]): string {
  if (!industries?.length) return ''
  return industries.slice(0, 3).join(' · ')
}
</script>

<template>
  <div class="connection-card" :class="{ 'connection-card--interactive': interactive }">
    <!-- Card header: logo + name -->
    <div class="mb-4 flex items-center gap-3">
      <div class="fund-logo" :class="{ 'fund-logo--image': candidate.subject?.logo_url }">
        <img
          v-if="candidate.subject?.logo_url"
          :src="candidate.subject.logo_url"
          :alt="candidate.subject?.name"
          class="h-full w-full rounded-[10px] object-contain"
        />
        <div v-else>{{ getInitials(candidate.subject?.name) }}</div>
      </div>
      <div>
        <div class="text-[15px] font-bold text-gray-900 dark:text-gray-200">
          {{ candidate.subject?.name }}
        </div>
        <div v-if="getGroupSubtitle(candidate)" class="mt-0.5 text-xs text-gray-500">
          {{ getGroupSubtitle(candidate) }}
        </div>
      </div>
    </div>

    <!-- Description (investment thesis) -->
    <p v-if="candidate.investment_thesis" class="mb-4 line-clamp-3 text-[13px] leading-relaxed text-gray-500">
      {{ candidate.investment_thesis }}
    </p>

    <!-- Team member photos -->
    <div v-if="candidate.team_members?.length" class="mb-4">
      <p class="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Team</p>
      <div class="flex items-center gap-1.5">
        <template v-for="member in candidate.team_members.slice(0, 5)" :key="member.id">
          <a
            v-if="member.linkedin_url"
            :href="member.linkedin_url"
            target="_blank"
            rel="noopener noreferrer"
            :title="`${member.name} — LinkedIn`"
            class="team-avatar-link"
          >
            <img
              v-if="member.photo_url"
              :src="member.photo_url"
              :alt="member.name"
              class="h-8 w-8 rounded-full object-cover"
            />
            <div v-else class="team-avatar-placeholder">{{ member.initials }}</div>
          </a>
          <div v-else :title="member.name">
            <img
              v-if="member.photo_url"
              :src="member.photo_url"
              :alt="member.name"
              class="h-8 w-8 rounded-full object-cover"
            />
            <div v-else class="team-avatar-placeholder">{{ member.initials }}</div>
          </div>
        </template>
        <span v-if="candidate.team_members.length > 5" class="text-xs text-gray-400">
          +{{ candidate.team_members.length - 5 }}
        </span>
      </div>
    </div>

    <!-- Meta chips -->
    <div
      v-if="
        getMinInvestmentLabel(candidate) ||
        getAccreditationLabel(candidate.accreditation_level_min) ||
        candidate.industries_interested?.length
      "
      class="flex flex-wrap gap-1.5 border-t border-gray-100 pt-4 dark:border-gray-700"
    >
      <span v-if="getMinInvestmentLabel(candidate)" class="meta-chip">
        {{ getMinInvestmentLabel(candidate) }}
      </span>
      <span v-if="getAccreditationLabel(candidate.accreditation_level_min)" class="meta-chip">
        {{ getAccreditationLabel(candidate.accreditation_level_min) }}
      </span>
      <span v-if="candidate.industries_interested?.length" class="meta-chip">
        {{ getIndustriesLabel(candidate.industries_interested) }}
      </span>
    </div>

    <!-- Actions slot -->
    <slot name="actions" />
  </div>
</template>

<style scoped>
.connection-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
}

.connection-card--interactive {
  transition: all 0.2s;
}

.connection-card--interactive:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #3b88af;
}

:root.dark .connection-card,
.dark .connection-card {
  background: #1f2937;
  border-color: #374151;
}

:root.dark .connection-card--interactive:hover,
.dark .connection-card--interactive:hover {
  border-color: #3b88af;
}

.fund-logo {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1a2332, #3b88af);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.fund-logo--image {
  background: white;
}

.meta-chip {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  padding: 3px 8px;
  border-radius: 4px;
}

:root.dark .meta-chip,
.dark .meta-chip {
  background: #374151;
  color: #9ca3af;
}

.team-avatar-link {
  display: block;
  border-radius: 9999px;
  transition: transform 0.15s ease;
}

.team-avatar-link:hover {
  transform: scale(1.1);
}

.team-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
}

:root.dark .team-avatar-placeholder,
.dark .team-avatar-placeholder {
  background: #374151;
  color: #9ca3af;
}
</style>
